import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";

export default function StaffDirectory() {
  const [staff, setStaff] = useState([]);
  const [query, setQuery] = useState("");
  const [department, setDepartment] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api("/api/staff")
      .then((data) => setStaff(Array.isArray(data) ? data : []))
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  const departments = useMemo(() => {
    return [...new Set(staff.map((person) => person.department_name).filter(Boolean))].sort();
  }, [staff]);

  const filtered = useMemo(() => {
    const q = query.toLowerCase();
    return staff.filter((person) => {
      const matchesDepartment = !department || person.department_name === department;
      const text = [person.full_name, person.department_name, person.position, person.email, person.research_interests].filter(Boolean).join(" ").toLowerCase();
      return matchesDepartment && text.includes(q);
    });
  }, [staff, query, department]);

  return (
    <main className="min-h-screen bg-white px-4 py-16">
      <section className="mx-auto max-w-6xl">
        <p className="text-sm font-bold uppercase tracking-[0.25em] text-emerald-700">Institution Directory</p>
        <h1 className="mt-3 text-4xl font-black text-slate-900">Staff Directory</h1>
        <p className="mt-3 max-w-2xl text-slate-600">Find staff, departments, units, offices, research areas and official contact details.</p>

        <div className="mt-8 grid gap-3 md:grid-cols-[1fr_280px]">
          <input
            className="w-full rounded-2xl border border-slate-300 p-4"
            placeholder="Search staff by name, department, position or research area..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <select value={department} onChange={(e) => setDepartment(e.target.value)} className="rounded-2xl border border-slate-300 p-4">
            <option value="">All departments</option>
            {departments.map((item) => <option key={item} value={item}>{item}</option>)}
          </select>
        </div>

        {error && <div className="mt-6 rounded-xl bg-red-50 p-4 font-semibold text-red-700">{error}</div>}

        {loading ? (
          <div className="mt-8 rounded-3xl bg-slate-50 p-8 text-slate-500">Loading staff directory...</div>
        ) : filtered.length === 0 ? (
          <div className="mt-8 rounded-3xl bg-slate-50 p-8 text-slate-500">No active staff profile found.</div>
        ) : (
          <div className="mt-8 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {filtered.map((person) => (
              <article key={person.id} className="rounded-3xl border border-slate-200 p-6 transition hover:-translate-y-1 hover:shadow-lg">
                {person.photo_url ? (
                  <img src={person.photo_url} alt={person.full_name} className="mb-4 h-24 w-24 rounded-2xl object-cover" />
                ) : (
                  <div className="mb-4 flex h-24 w-24 items-center justify-center rounded-2xl bg-emerald-100 text-2xl font-black text-emerald-700">
                    {person.full_name?.[0] || "S"}
                  </div>
                )}
                <h2 className="text-xl font-black text-slate-900">{person.title ? `${person.title} ` : ""}{person.full_name}</h2>
                <p className="mt-1 font-semibold text-emerald-700">{person.position}</p>
                <p className="mt-1 text-sm text-slate-500">{person.department_name}</p>
                <p className="mt-3 text-sm text-slate-600">{person.office}</p>
                {person.email && <a href={`mailto:${person.email}`} className="mt-4 inline-block text-sm font-bold text-slate-900">{person.email}</a>}
                {person.slug && <Link to={`/staff/${person.slug}`} className="mt-5 block rounded-xl bg-slate-950 px-4 py-3 text-center text-sm font-bold text-white hover:bg-emerald-700">View Profile</Link>}
              </article>
            ))}
          </div>
        )}
      </section>
    </main>
  );
}
